/**
 * Client for cty-ai-data-access's admin API (DOC → Sources → Data). The contract is
 * cty-ai-data-access/docs/API.md, section "Admin API (DOC → Sources → Data)".
 *
 * The browser calls the service's own host with the signed-in user's DOC token. The service takes
 * the tenant from the token and allows a request only for members of the tenant's admin-group;
 * GET /v1/admin/me answers every signed-in user, so DOC can decide whether to show the tabs.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTypedSelector } from '@circuitry-ai/doc-data';

/** The service's base URL; '' when the admin tabs are off (not configured, or a production build). */
export function getAiDataAccessUrl(): string {
  if (import.meta.env.CTY_MODE === 'production') return '';
  return (import.meta.env.CTY_AI_DATA_ACCESS_URL ?? '').trim().replace(/\/+$/, '');
}

export type RowScope = 'none' | 'own' | 'group' | 'assigned' | 'all';
/** Every row scope, in the order the matrix offers them. */
export const ROW_SCOPES: readonly RowScope[] = ['none', 'own', 'group', 'assigned', 'all'];

/** How a column reaches an agent. */
export type ColumnAction = 'allow' | 'mask' | 'partial' | 'pseudonymize' | 'omit';

export interface AdminMe {
  tenant_id: string;
  user_sub: string;
  admin: boolean;
}

export interface CatalogColumn {
  name: string;
  type: string;
  description: string;
  classification: string;
  action: ColumnAction;
}

export interface CatalogDataset {
  name: string;
  description: string;
  grain: string;
  time_column: string | null;
  database: string;
  tenant_isolation: string;
  owner: { column: string; identifies_by: string[] } | null;
  entity: { column: string; type: string } | null;
  /** The row scopes this dataset supports. */
  scopes: RowScope[];
  columns: CatalogColumn[];
  /** Columns that are never exposed and not listed. */
  excluded_columns: number;
}

export interface CatalogMetric {
  key: string;
  /** The dataset it reads, as the catalog names it (short or full name). */
  dataset: string;
  description: string;
  params: string[];
  certified: boolean;
  version: number | null;
  owner: string | null;
}

export interface CatalogDomain {
  domain: string;
  description: string;
  datasets: CatalogDataset[];
  metrics: CatalogMetric[];
}

export interface Catalog {
  domains: CatalogDomain[];
}

export interface AdminGroup {
  name: string;
  kind: 'people' | 'agents';
  members: number;
}

/** One cell of the matrix: `object` is `data_domain:<domain>` or `dataset:<dataset name>`. */
export interface Grant {
  object: string;
  group: string;
  scopes: RowScope[];
  sensitive: boolean;
}

export interface GrantChange {
  object: string;
  group: string;
  scope: RowScope;
  sensitive: boolean;
}

export interface GrantChangeResult {
  grant: Grant;
  before: Grant;
}

export interface AdminUser {
  user_id: string;
  name: string;
  email: string;
  aliases: string[];
}

export interface AdminAgent {
  id: string;
  label: string;
  group: string;
}

export interface CheckRequest {
  user_id: string;
  agent: string;
  sql: string | null;
  filters: unknown | null;
}

export interface CheckDataset {
  row_scope: string;
  /** Visible columns and how each is shown. */
  columns: Record<string, ColumnAction>;
  hidden_columns: string[];
  notes: string[];
}

export interface CheckResult {
  user: { user_id: string; name: string; email: string };
  agent: { id: string; kind: string };
  datasets: Record<string, CheckDataset>;
  rewritten_sql: string | null;
  filters: unknown | null;
  /** How the user was matched (no username: it is known only from their own sign-in). */
  identity_note?: string;
}

export type Decision = 'allow' | 'deny' | 'error';

export interface ActivityEvent {
  at: string;
  query_id: string;
  user_sub: string;
  agent_id: string | null;
  agent_kind: string | null;
  purpose: string | null;
  operation: string;
  decision: Decision;
  reason_code: string | null;
  datasets: string[];
  row_count: number | null;
  row_filters?: unknown;
  masked?: unknown;
  omitted?: unknown;
  latency_ms: number | null;
}

export interface ActivityPage {
  events: ActivityEvent[];
  /** Pass as `before` for the next older page; null when there are no older events. */
  next_before: string | null;
}

export interface ActivityQuery {
  limit?: number;
  before?: string;
  decision?: Decision;
  operation?: string;
  user_sub?: string;
}

/** The matrix's object ids. */
export const domainObject = (domain: string) => `data_domain:${domain}`;
export const datasetObject = (dataset: string) => `dataset:${dataset}`;

/**
 * Thrown for a non-2xx answer. `message` is the API's own message; `code` its error code (e.g.
 * `not_admin`, `bad_grant`, or a query rejection code on /check); status 0 means not reached.
 */
export class AdminApiError extends Error {
  constructor(message: string, readonly status: number, readonly code: string | null = null) {
    super(message);
    this.name = 'AdminApiError';
  }
}

export const UNREACHABLE = 'The data access service can’t be reached right now. Try again in a few minutes.';

async function call<T>(baseUrl: string, path: string, token: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: { ...(init.body ? { 'Content-Type': 'application/json' } : {}), Authorization: `Bearer ${token}` },
    });
  } catch (error) {
    if ((error as Error)?.name === 'AbortError') throw error;
    throw new AdminApiError(UNREACHABLE, 0);
  }
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const message = typeof body?.message === 'string' && body.message ? body.message : `HTTP ${response.status}`;
    const code = typeof body?.code === 'string' ? body.code : null;
    throw new AdminApiError(message, response.status, code);
  }
  return body as T;
}

/** The admin routes, bound to one base URL and token. */
export function createAdminClient(baseUrl: string, token: string) {
  const get = <T>(path: string, signal?: AbortSignal) => call<T>(baseUrl, path, token, { signal });
  const send = <T>(method: 'PUT' | 'POST', path: string, body: unknown, signal?: AbortSignal) =>
    call<T>(baseUrl, path, token, { method, body: JSON.stringify(body), signal });
  return {
    me: (signal?: AbortSignal) => get<AdminMe>('/v1/admin/me', signal),
    catalog: (signal?: AbortSignal) => get<Catalog>('/v1/admin/catalog', signal),
    groups: async (signal?: AbortSignal) => (await get<{ groups: AdminGroup[] }>('/v1/admin/groups', signal)).groups ?? [],
    grants: async (signal?: AbortSignal) => (await get<{ grants: Grant[] }>('/v1/admin/grants', signal)).grants ?? [],
    setGrant: (change: GrantChange, signal?: AbortSignal) => send<GrantChangeResult>('PUT', '/v1/admin/grants', change, signal),
    users: async (signal?: AbortSignal) => (await get<{ users: AdminUser[] }>('/v1/admin/users', signal)).users ?? [],
    setAliases: (userId: string, aliases: string[], signal?: AbortSignal) =>
      send<{ user_id: string; aliases: string[] }>('PUT', `/v1/admin/aliases/${encodeURIComponent(userId)}`, { aliases }, signal),
    agents: async (signal?: AbortSignal) => (await get<{ agents: AdminAgent[] }>('/v1/admin/agents', signal)).agents ?? [],
    check: (request: CheckRequest, signal?: AbortSignal) => send<CheckResult>('POST', '/v1/admin/check', request, signal),
    activity: (query: ActivityQuery = {}, signal?: AbortSignal) => {
      const params = new URLSearchParams();
      for (const [key, value] of Object.entries(query)) {
        if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
      }
      const search = params.toString();
      return get<ActivityPage>(`/v1/admin/activity${search ? `?${search}` : ''}`, signal);
    },
  };
}

export type AdminClient = ReturnType<typeof createAdminClient>;

/** The client for the signed-in user (null until there is a token). */
export function useAdminClient(baseUrl: string): AdminClient | null {
  const token = useTypedSelector((state) => state.auth.token) as string | null | undefined;
  return useMemo(() => (baseUrl && token ? createAdminClient(baseUrl, token) : null), [baseUrl, token]);
}

/**
 * Whether the signed-in user administers their tenant's data access: false while it is being
 * asked, when the service is not configured, and when /v1/admin/me fails. Asked once per page.
 */
export function useIsDataAccessAdmin(baseUrl: string): boolean {
  const client = useAdminClient(baseUrl);
  const [admin, setAdmin] = useState(false);
  const clientRef = useRef(client);
  clientRef.current = client;
  const ready = Boolean(client);
  useEffect(() => {
    const current = clientRef.current;
    if (!ready || !current) return undefined;
    const request = new AbortController();
    current.me(request.signal)
      .then((me) => { if (!request.signal.aborted) setAdmin(me?.admin === true); })
      .catch(() => { if (!request.signal.aborted) setAdmin(false); });
    return () => request.abort();
  }, [ready, baseUrl]);
  return ready && admin;
}
